import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, Copy, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ApiError } from '@/lib/api';
import {
  EMPTY_CREATE_COMPANY_DRAFT,
  hasErrors,
  slugFromName,
  validateCreateCompany,
  type CreateCompanyDraft,
  type CreateCompanyErrors,
} from './companyOnboarding';
import {
  useCreateCompany,
  type CreateCompanyResult,
} from './hooks/usePlatformAdminQueries';

interface CreateCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Подпись ошибки поля — с ролью alert, чтобы её озвучил скринридер. */
function FieldError({ id, text }: { id: string; text?: string }) {
  if (!text) return null;
  return (
    <p id={id} role="alert" className="text-xs text-destructive">
      {text}
    </p>
  );
}

/**
 * «Создать компанию» — платформенный онбординг нового клиента (AK-572).
 *
 * До этой формы кнопка в реестре писала в консоль, а компании заводились
 * `docker compose exec … create_tenant.py`. Ручка `POST /platform/companies`
 * при этом жила с самого начала — не хватало ровно диалога.
 *
 * Два решения, которые стоит держать в голове при правках:
 *
 *  * **slug не прячем.** Он попадает в имя схемы (`tenant_<slug>`) и потом
 *    всплывает в логах и psql — человеку полезно видеть и уметь поправить
 *    то, что придумал транслит. Пока адрес не трогали руками, он едет за
 *    названием; первая ручная правка связь рвёт (иначе набранный адрес
 *    затирался бы следующей буквой названия).
 *  * **ссылка-приглашение показывается один раз.** Сервер отдаёт её только в
 *    ответе на создание, поэтому после создания с галкой «без письма» диалог
 *    не закрывается сам: закроешь — взять ссылку будет неоткуда. (В базе по
 *    этому пути лежит сырой токен, а не хеш, — но это дефект хранения
 *    `create_tenant.py`, который чинится отдельно, и строить на нём «покажем
 *    ещё раз» нельзя.)
 */
export function CreateCompanyDialog({
  open,
  onOpenChange,
}: CreateCompanyDialogProps) {
  const [draft, setDraft] = useState<CreateCompanyDraft>(
    EMPTY_CREATE_COMPANY_DRAFT,
  );
  const [slugTouched, setSlugTouched] = useState(false);
  const [errors, setErrors] = useState<CreateCompanyErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateCompanyResult | null>(null);
  const [copied, setCopied] = useState(false);
  const createCompany = useCreateCompany();
  const nameRef = useRef<HTMLInputElement>(null);

  // Сброс при каждом открытии: диалог живёт в дереве постоянно, и без этого
  // второй заход показал бы данные предыдущей компании.
  useEffect(() => {
    if (open) {
      setDraft(EMPTY_CREATE_COMPANY_DRAFT);
      setSlugTouched(false);
      setErrors({});
      setSubmitError(null);
      setCreated(null);
      setCopied(false);
    }
  }, [open]);

  const set = <K extends keyof CreateCompanyDraft>(
    key: K,
    value: CreateCompanyDraft[K],
  ) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const onNameChange = (value: string) => {
    setDraft((prev) => ({
      ...prev,
      name: value,
      slug: slugTouched ? prev.slug : slugFromName(value),
    }));
  };

  const submit = async () => {
    const found = validateCreateCompany(draft);
    setErrors(found);
    setSubmitError(null);
    if (hasErrors(found)) return;

    try {
      // При «пригласить позже» контакты не шлём вовсе: иначе сервер завёл бы
      // приглашение на полупустые данные, оставленные в форме до переключения.
      const result = await createCompany.mutateAsync({
        slug: draft.slug,
        name: draft.name.trim(),
        email_domain: draft.emailDomain.trim() || null,
        admin_email: draft.inviteAdmin ? draft.adminEmail.trim() : null,
        admin_first_name: draft.inviteAdmin
          ? draft.adminFirstName.trim() || null
          : null,
        admin_last_name: draft.inviteAdmin
          ? draft.adminLastName.trim() || null
          : null,
        personal_message: draft.inviteAdmin
          ? draft.personalMessage.trim() || null
          : null,
        skip_email: draft.inviteAdmin ? draft.skipEmail : false,
      });
      setCreated(result);
      // Письмо ушло — показывать больше нечего, закрываем сразу.
      if (!result.invite_link) onOpenChange(false);
    } catch (err) {
      setSubmitError(
        err instanceof ApiError
          ? err.detail || 'Не удалось создать компанию'
          : 'Не удалось создать компанию',
      );
    }
  };

  const copyLink = async () => {
    if (!created?.invite_link) return;
    try {
      await navigator.clipboard.writeText(created.invite_link);
      setCopied(true);
    } catch {
      // Буфер недоступен (нет https / отказали в доступе) — ссылка и так на
      // экране в поле, откуда её можно выделить руками. Молча не падаем.
      setCopied(false);
    }
  };

  const busy = createCompany.isPending;

  return (
    <Dialog open={open} onOpenChange={busy ? () => {} : onOpenChange}>
      <DialogContent
        /* max-h + прокрутка по паттерну SowWizard: на 1366×768 форма из семи
           полей выше окна, и без этого «Создать компанию» уезжала за нижний
           край — диалог центрируется по вертикали, обрезая себя с обеих
           сторон. dvh, а не vh: на мобиле панель браузера съедает высоту. */
        className="max-h-[85dvh] max-w-lg overflow-y-auto"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          nameRef.current?.focus();
        }}
      >
        {created?.invite_link ? (
          <>
            <DialogHeader>
              <DialogTitle>Компания «{created.name}» создана</DialogTitle>
              <DialogDescription>
                Письмо не отправляли. Передайте ссылку первому администратору
                любым удобным способом — второй раз показать её не получится.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="invite-link">Ссылка-приглашение</Label>
              <div className="flex gap-2">
                <Input
                  id="invite-link"
                  readOnly
                  value={created.invite_link}
                  onFocus={(e) => e.currentTarget.select()}
                  className="font-mono text-xs"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void copyLink()}
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copied ? 'Скопировано' : 'Копировать'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Адрес компании: <span className="font-mono">{created.slug}</span>
              </p>
            </div>

            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Готово</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Новая компания</DialogTitle>
              <DialogDescription>
                Заведём компанию и пригласим её первого администратора — дальше
                людей он позовёт сам.
              </DialogDescription>
            </DialogHeader>

            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="company-name">Название компании</Label>
                <Input
                  id="company-name"
                  ref={nameRef}
                  value={draft.name}
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder="Название компании"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'company-name-error' : undefined}
                />
                <FieldError id="company-name-error" text={errors.name} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company-slug">Адрес компании</Label>
                <Input
                  id="company-slug"
                  value={draft.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    set('slug', e.target.value.trim().toLowerCase());
                  }}
                  placeholder="agrofirma"
                  className="font-mono"
                  aria-invalid={!!errors.slug}
                  aria-describedby="company-slug-hint"
                />
                <p id="company-slug-hint" className="text-xs text-muted-foreground">
                  Подставили из названия. Латиницей, менять после создания
                  нельзя — адрес уходит в имя схемы данных компании.
                </p>
                <FieldError id="company-slug-error" text={errors.slug} />
              </div>

              <fieldset className="space-y-3 rounded-md border border-border p-3">
                <legend className="px-1 text-sm font-medium">
                  Администратор компании
                </legend>

                <RadioGroup
                  value={draft.inviteAdmin ? 'now' : 'later'}
                  onValueChange={(v) => set('inviteAdmin', v === 'now')}
                  className="gap-2"
                >
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="now" id="invite-now" />
                    <Label htmlFor="invite-now" className="font-normal leading-6">
                      Пригласить сейчас
                    </Label>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="later" id="invite-later" />
                    <Label
                      htmlFor="invite-later"
                      className="font-normal leading-6"
                    >
                      Пригласить позже
                    </Label>
                  </div>
                </RadioGroup>

                {draft.inviteAdmin ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="admin-email">Почта</Label>
                      <Input
                        id="admin-email"
                        type="email"
                        value={draft.adminEmail}
                        onChange={(e) => set('adminEmail', e.target.value)}
                        placeholder="director@company.ru"
                        aria-invalid={!!errors.adminEmail}
                        aria-describedby={
                          errors.adminEmail ? 'admin-email-error' : undefined
                        }
                      />
                      <FieldError id="admin-email-error" text={errors.adminEmail} />
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="admin-first-name">Имя</Label>
                        <Input
                          id="admin-first-name"
                          value={draft.adminFirstName}
                          onChange={(e) => set('adminFirstName', e.target.value)}
                          placeholder="Иван"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="admin-last-name">Фамилия</Label>
                        <Input
                          id="admin-last-name"
                          value={draft.adminLastName}
                          onChange={(e) => set('adminLastName', e.target.value)}
                          placeholder="Иванов"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="personal-message">
                        Сообщение в приглашении — необязательно
                      </Label>
                      <Input
                        id="personal-message"
                        value={draft.personalMessage}
                        onChange={(e) => set('personalMessage', e.target.value)}
                        placeholder="Добро пожаловать! Пишите, если что-то не открывается."
                      />
                    </div>

                    <div className="flex items-start gap-2">
                      <Checkbox
                        id="skip-email"
                        checked={draft.skipEmail}
                        onCheckedChange={(v) => set('skipEmail', v === true)}
                      />
                      <Label
                        htmlFor="skip-email"
                        className="text-sm font-normal leading-6"
                      >
                        Не отправлять письмо — покажите ссылку, передам сам
                      </Label>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Компания будет создана пустой — с холдингом, компанией и
                    сезоном, но без людей. Пригласить администратора можно в
                    любой момент: раскройте строку компании в реестре, вкладка
                    «Приглашения».
                  </p>
                )}
              </fieldset>

              {submitError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={busy}
                >
                  Отмена
                </Button>
                <Button type="submit" disabled={busy}>
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {busy ? 'Создаём…' : 'Создать компанию'}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CreateCompanyDialog;
